import type { EmployeeStatusFilter } from '@/api/handlers/resources/salaries/requests';
import { bpsToPercentText } from '@/api/handlers/resources/salaries';
import { isSalaryErrorCode } from '@/api/handlers/resources/salaries/salariesMapping';
import type { ErrorResponse } from '@/api/handlers/responses';
import type {
    ChartAccount,
    Employee,
    EmployeeRegisterSummary,
    EmployeeStatus,
    JournalEntryStatus,
    LocaleCode,
    PayrollLine,
    PayrollLineReason,
    PayrollRunSummary,
    PayrollTotals,
    TaxRates,
} from '@/api/models';
import {
    formatGel,
    ledgerAccountCode,
    ledgerNaturalTetri,
} from '@/pages/accounting/books/chartOfAccounts/utils';
import {
    isoToDisplayDate,
    NIL_AMOUNT,
    resolveLabel,
} from '@/pages/accounting/statements/statementMeta';
import { monthLabelKey } from '@/pages/accounting/configurations/periods/periodsMeta';

/**
 * Pure rules and presentational config for Salaries.
 *
 * Everything derived lives here so the views stay render-only, and so the things
 * that must not be got wrong — the tie-out, the employer-cost identity, and the
 * separation of the state contribution from the entry — are plain functions with
 * tests rather than JSX.
 */

// ── status ──────────────────────────────────────────────────────────────────

/**
 * Status pill styling in the journal's calm vocabulary. **Leaving is ordinary
 * bookkeeping, not an error**: Terminated reads as a settled, neutral state,
 * never as danger.
 */
export const EMPLOYEE_STATUS_CHIP: Record<
    EmployeeStatus,
    { className: string; labelKey: string }
> = {
    ACTIVE: {
        className: 'bg-success-soft text-success-soft-foreground',
        labelKey: 'salaries.status.active',
    },
    TERMINATED: {
        className: 'bg-surface-secondary text-muted',
        labelKey: 'salaries.status.terminated',
    },
};

/**
 * The status filter's options, in the order they appear. Config, not markup —
 * the project keeps presentational maps in `.ts` so the views stay render-only.
 */
export const EMPLOYEE_STATUS_FILTERS: {
    id: EmployeeStatusFilter;
    labelKey: string;
}[] = [
    { id: 'ACTIVE', labelKey: 'salaries.filters.active' },
    { id: 'TERMINATED', labelKey: 'salaries.filters.terminated' },
    { id: 'ALL', labelKey: 'salaries.filters.all' },
];

/** i18n key explaining what an employee's line in a run reflects. */
// `OK` is absent by design: a row that ran normally carries no
// explanation (`reasonKey` is null), and a key mapped to an empty
// string is a label waiting to be rendered blank.
export const RUN_REASON_KEY: Record<
    Exclude<PayrollLineReason, 'OK'>,
    string
> = {
    NOT_YET_HIRED: 'salaries.run.reasons.notYetHired',
    ALREADY_LEFT: 'salaries.run.reasons.alreadyLeft',
    PRORATED: 'salaries.run.reasons.prorated',
    ZERO_SALARY: 'salaries.run.reasons.zeroSalary',
};

/** Copy + icon for each way the register can come back with no rows. */
export const REGISTER_EMPTY_COPY: Record<
    Exclude<RegisterEmptyState, 'NONE'>,
    { titleKey: string; bodyKey: string; icon: string }
> = {
    NO_EMPLOYEES: {
        titleKey: 'salaries.empty.noEmployees.title',
        bodyKey: 'salaries.empty.noEmployees.body',
        icon: 'gravity-ui:persons',
    },
    // An all-terminated register is not empty — it holds real history and every
    // employee has simply left. Saying "No employees yet" would be untrue.
    ALL_TERMINATED: {
        titleKey: 'salaries.empty.allTerminated.title',
        bodyKey: 'salaries.empty.allTerminated.body',
        icon: 'gravity-ui:archive',
    },
    NO_MATCHES: {
        titleKey: 'salaries.empty.noMatches.title',
        bodyKey: 'salaries.empty.noMatches.body',
        icon: 'gravity-ui:magnifier',
    },
};

/** Shared header cell classes for the register table. */
export const REGISTER_TEXT_HEADER =
    'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted';
export const REGISTER_NUMERIC_HEADER =
    'px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-muted';

// ── the statutory rates, as display text ────────────────────────────────────

/** The four rates payroll applies, as percentages, ready to interpolate. */
export type RateText = {
    incomeTax: string;
    employeePension: string;
    employerPension: string;
    state: string;
};

/**
 * The rates the screen quotes, from the **effective-dated rate set** that
 * governs the figures being shown.
 *
 * There is no constant behind this any more. Rates are resolved server-side from
 * the `TaxRateSet` in force on the period's end date for a run, and on today's
 * date for the register — so a run of a past month applies the rates that month
 * was subject to, and a column header that hardcoded 20% would be captioning
 * someone else's arithmetic.
 *
 * `null` when the set could not be read (nothing recorded yet, or a period that
 * predates the earliest set). The caller then omits the rate from the label
 * rather than falling back to a number: a rate quoted beside a figure it did not
 * produce is worse than no rate at all.
 */
export const rateTextOf = (rates: TaxRates | null): RateText | null =>
    rates === null
        ? null
        : {
              incomeTax: bpsToPercentText(rates.personalIncomeTaxBps),
              employeePension: bpsToPercentText(rates.employeePensionBps),
              employerPension: bpsToPercentText(rates.employerPensionBps),
              state: bpsToPercentText(rates.statePensionBps),
          };

/**
 * A column label, with its rate only when one is known.
 *
 * Two i18n keys per column — `…columns.incomeTax` and
 * `…columns.incomeTaxNoRate` — rather than interpolating an empty string, so
 * neither reading leaves a stray "()" behind.
 */
export const rateLabel = (
    baseKey: string,
    rate: string | undefined,
    t: (key: string, values?: Record<string, unknown>) => string,
): string => (rate ? t(baseKey, { rate }) : t(`${baseKey}NoRate`));

// ── periods ─────────────────────────────────────────────────────────────────

/** The `YYYY-MM` period an ISO date falls in. */
export const periodFromDate = (isoDate: string): string => isoDate.slice(0, 7);

/**
 * `<Month> <Year>` for a `YYYY-MM`, using the app's own month strings.
 *
 * Deliberately not `Intl` and not dayjs: dayjs is loaded with no locale packs
 * (so `ka` silently renders English) and a Chromium build verified in testing
 * resolved `ka` to `en-US`. The twelve strings already live in the locale files
 * under `periods.months.*` — reuse them (see `periodsMeta`).
 */
export const formatPeriod = (
    period: string,
    t: (key: string) => string,
): string => `${t(monthLabelKey(period))} ${period.slice(0, 4)}`;

// ── personal ID ─────────────────────────────────────────────────────────────

/** The Georgian personal number: exactly 11 digits. */
const PERSONAL_ID_PATTERN = /^\d{11}$/;

export const isValidPersonalId = (value: string): boolean =>
    PERSONAL_ID_PATTERN.test(value.trim());

/**
 * Whether another employee in the **loaded** register already holds this personal
 * number.
 *
 * A client-side check over the rows on screen, so the user is told before they
 * submit; the server is the final authority and its `409 personalId: DUPLICATE`
 * lands on the same field. Both are needed — the loaded page may not hold every
 * employee, and a duplicate found only on submit is a worse experience than one
 * found while typing.
 */
export const isPersonalIdTaken = (
    personalId: string,
    employees: Employee[],
    selfId: string | null,
): boolean => {
    const wanted = personalId.trim();
    return employees.some(
        (employee) => employee.id !== selfId && employee.personalId === wanted,
    );
};

// ── the tie-out ─────────────────────────────────────────────────────────────

/**
 * One comparison: what payroll has **accrued** against what the ledger holds on
 * the account that accrual credits.
 *
 * The two are not required to be equal, and that is the point. The register only
 * ever sees what payroll charged; paying employees and remitting withholdings
 * are ordinary journal entries it never learns about, so once a payment is
 * posted the ledger balance falls and the accrued figure stays. A difference is
 * therefore *the payments* — or a mis-posting — and either way it is worth
 * looking at, which is why this is stated rather than hidden.
 */
export type TieOutFigure = {
    labelKey: string;
    slot: string;
    /** What posted payroll runs have charged. */
    accruedTetri: number;
    /** The account's own balance, in its natural direction. */
    ledgerTetri: number;
    /** accrued − ledger. Zero when they match. */
    differenceTetri: number;
    matches: boolean;
    /** The account code the comparison read, for the "compared against" caption. */
    accountCode: string;
    /**
     * Whether the ledger side could be read at all.
     *
     * `false` in two cases, and both mean the same thing to the reader: the
     * register names no account for this role (the chart has nothing tagged for
     * it), or it names one the loaded chart does not hold. There is then nothing
     * to compare against, so the row says so rather than reporting a difference
     * equal to the whole accrual. `matches` is forced true so an unknown ledger
     * can never colour the screen as a mismatch.
     */
    isResolved: boolean;
};

export type SalariesTieOut = {
    figures: TieOutFigure[];
    /** False when **no** side could be resolved — nothing to compare at all. */
    isResolved: boolean;
    /** True only when every resolved side matches to the tetri. */
    inAgreement: boolean;
};

/**
 * The **natural** balance of one account.
 *
 * Natural is the balance in the account's own normal direction, so a
 * credit-normal payable reports what is owed as a positive magnitude — the same
 * sense the register carries its accrued figures in. Using the signed
 * (debit-positive) figure here would make every comparison sign-flipped.
 */
const naturalTetriFor = (
    accounts: ChartAccount[],
    accountId: string,
): number | null => ledgerNaturalTetri(accounts, accountId);

const toFigure = (
    labelKey: string,
    slot: string,
    accruedTetri: number,
    accounts: ChartAccount[],
    /** Null when the chart carries no account tagged for this payroll role. */
    accountId: string | null,
): TieOutFigure => {
    const ledgerTetri =
        accountId === null ? null : naturalTetriFor(accounts, accountId);
    const accountCode =
        accountId === null ? '' : ledgerAccountCode(accounts, accountId);

    if (ledgerTetri === null) {
        return {
            labelKey,
            slot,
            accruedTetri,
            ledgerTetri: 0,
            differenceTetri: 0,
            matches: true,
            accountCode,
            isResolved: false,
        };
    }

    const differenceTetri = accruedTetri - ledgerTetri;
    return {
        labelKey,
        slot,
        accruedTetri,
        ledgerTetri,
        differenceTetri,
        matches: differenceTetri === 0,
        accountCode,
        isResolved: true,
    };
};

/**
 * Compare what payroll has accrued against what the ledger holds.
 *
 * This is the point of the screen, and BOS-109 is what made it real: with the
 * register live, both sides describe the same books. The accrued figures come
 * from the register's own summary — derived from run lines whose entry has
 * **posted** — and the ledger figures from the **existing** accounts /
 * trial-balance query, so the register is never allowed to certify itself: it
 * names the accounts, it does not supply their balances.
 *
 * Unlike the fixed-asset register there is no server-side `/reconciliation` here,
 * so the comparison is assembled client-side from those two feeds. And unlike a
 * true tie-out, a difference is not necessarily an error: payments and
 * remittances move the ledger and the register never sees them. The screen
 * therefore reports the difference and lets a bookkeeper read it, rather than
 * calling it a failure.
 *
 * Returns `null` while the chart is still loading — an unknown ledger is not the
 * same as a disagreeing one, and must never be drawn as a difference.
 */
export const computeTieOut = (
    summary: EmployeeRegisterSummary,
    accounts: ChartAccount[] | null,
): SalariesTieOut | null => {
    if (accounts === null) {
        return null;
    }

    const figures: TieOutFigure[] = [
        toFigure(
            'salaries.tieOut.netPay',
            'net-pay',
            summary.accruedNetPayTetri,
            accounts,
            summary.salaryPayableAccountId,
        ),
        toFigure(
            'salaries.tieOut.incomeTax',
            'income-tax',
            summary.accruedIncomeTaxTetri,
            accounts,
            summary.incomeTaxPayableAccountId,
        ),
        toFigure(
            'salaries.tieOut.pension',
            'pension',
            summary.accruedPensionTetri,
            accounts,
            summary.pensionPayableAccountId,
        ),
    ];

    // One unresolved row is not a reason to withhold the other two: a chart
    // missing the pension-payable tag still supports the net-pay comparison, and
    // that comparison is worth having. Only when *nothing* resolves is there
    // nothing to show.
    const isResolved = figures.some((figure) => figure.isResolved);

    return {
        figures,
        isResolved,
        inAgreement: isResolved && figures.every((figure) => figure.matches),
    };
};

// ── register rows ───────────────────────────────────────────────────────────

/** A render-ready register row. All derivation happens here, none in the View. */
export type EmployeeRow = {
    id: string;
    /** 1-based position in the visible list (the `#` column). */
    index: number;
    name: string;
    personalId: string;
    positionLabel: string;
    hireDateText: string;
    grossSalaryText: string;
    pensionParticipant: boolean;
    status: EmployeeStatus;
    isTerminated: boolean;
    /** Non-empty whenever a departure is dated — past **or** future. */
    terminationDateText: string;
    /**
     * Dated to leave, but not gone yet.
     *
     * `status` is derived from whether the last day has **passed**, so someone
     * leaving in December is `ACTIVE` today, counts toward the monthly cost, and
     * is charged a full month until they go. The row says "leaving <date>"
     * rather than inventing a third status the API does not have.
     */
    isLeaving: boolean;
};

/**
 * Map employees to rows. A missing position reads as an en dash rather than a
 * blank cell, so "no title recorded" is visible rather than ambiguous.
 */
export const toEmployeeRows = (
    employees: Employee[],
    locale: LocaleCode,
): EmployeeRow[] =>
    employees.map((employee, index) => ({
        id: employee.id,
        index: index + 1,
        name: resolveLabel(employee.name, locale, employee.id),
        personalId: employee.personalId,
        positionLabel: resolveLabel(employee.position, locale, NIL_AMOUNT),
        hireDateText: isoToDisplayDate(employee.hireDate),
        grossSalaryText: formatGel(employee.grossSalaryTetri),
        pensionParticipant: employee.pensionParticipant,
        status: employee.status,
        isTerminated: employee.status === 'TERMINATED',
        terminationDateText: employee.terminationDate
            ? isoToDisplayDate(employee.terminationDate)
            : '',
        isLeaving:
            employee.status === 'ACTIVE' && employee.terminationDate !== null,
    }));

/** Summary card figures, pre-formatted. */
export type SummaryFigures = {
    activeEmployeeCount: number;
    monthlyGrossText: string;
    employerPensionText: string;
    employerCostText: string;
};

/**
 * The three headline figures.
 *
 * **Employer cost is `gross + employer 2%`** — the employee's own withholdings
 * are already inside gross and adding them here would overstate the cost by 22%.
 * The server computes it; this only formats it, and the identity is asserted in
 * the mock's tests.
 */
export const toSummaryFigures = (
    summary: EmployeeRegisterSummary,
): SummaryFigures => ({
    activeEmployeeCount: summary.activeEmployeeCount,
    monthlyGrossText: formatGel(summary.monthlyGrossTetri),
    employerPensionText: formatGel(summary.monthlyEmployerPensionTetri),
    employerCostText: formatGel(summary.monthlyEmployerCostTetri),
});

/**
 * Which empty-ish state the register is in.
 *
 * An **all-terminated** register is not empty — it holds real history and every
 * employee has simply left. Saying "No employees yet" there would be a lie, so it
 * gets its own state.
 */
export type RegisterEmptyState =
    'NONE' | 'NO_EMPLOYEES' | 'ALL_TERMINATED' | 'NO_MATCHES';

export const registerEmptyState = (params: {
    visibleCount: number;
    activeCount: number;
    hasSearch: boolean;
    /** The register holds nothing at all, whatever the filter says. */
    totalCount: number;
}): RegisterEmptyState => {
    if (params.visibleCount > 0) {
        return 'NONE';
    }
    if (params.hasSearch) {
        return 'NO_MATCHES';
    }
    if (params.totalCount === 0) {
        return 'NO_EMPLOYEES';
    }
    return params.activeCount === 0 ? 'ALL_TERMINATED' : 'NO_MATCHES';
};

// ── payroll-run preview ─────────────────────────────────────────────────────

/** A render-ready preview row: the five amounts, or the zero and why. */
export type RunPreviewRow = {
    employeeId: string;
    name: string;
    grossText: string;
    incomeTaxText: string;
    employeePensionText: string;
    employerPensionText: string;
    netText: string;
    /** Information only — never part of the entry. */
    stateContributionText: string;
    /** Null when the line is an ordinary full-month charge. */
    reasonKey: string | null;
    /** Interpolation for a prorated line: `{{days}} / {{total}}`. */
    prorationDays: { days: number; total: number } | null;
    isZero: boolean;
    isProrated: boolean;
};

const amountText = (tetri: number): string =>
    tetri === 0 ? NIL_AMOUNT : formatGel(tetri);

export const toRunPreviewRows = (
    lines: PayrollLine[],
    locale: LocaleCode,
): RunPreviewRow[] =>
    lines.map((line) => ({
        employeeId: line.employeeId,
        name: resolveLabel(line.employeeName, locale, line.employeeId),
        grossText: amountText(line.grossTetri),
        incomeTaxText: amountText(line.incomeTaxTetri),
        employeePensionText: amountText(line.employeePensionTetri),
        employerPensionText: amountText(line.employerPensionTetri),
        netText: amountText(line.netTetri),
        stateContributionText: amountText(line.stateContributionTetri),
        // 'OK' has no copy — an ordinary line needs no explanation, and rendering
        // an empty string under every name is noise.
        reasonKey: line.reason === 'OK' ? null : RUN_REASON_KEY[line.reason],
        prorationDays: line.isProrated
            ? { days: line.daysEmployed, total: line.daysInPeriod }
            : null,
        isZero: line.grossTetri === 0,
        isProrated: line.isProrated,
    }));

/** The preview's footer row, pre-formatted. */
export type RunTotalsRow = {
    grossText: string;
    incomeTaxText: string;
    employeePensionText: string;
    employerPensionText: string;
    netText: string;
    stateContributionText: string;
    employerCostText: string;
    /** Debit and credit side of the entry the run will build — always equal. */
    entryTotalText: string;
    paidEmployeeCount: number;
    hasStateContribution: boolean;
};

/**
 * The entry's total, either side: `gross + employer pension`.
 *
 * Deliberately derived from the same totals the draft's lines are built from, so
 * the figure the dialog shows and the figure the entry carries cannot disagree.
 */
export const entryTotalTetri = (totals: PayrollTotals): number =>
    totals.grossTetri + totals.employerPensionTetri;

export const toRunTotalsRow = (totals: PayrollTotals): RunTotalsRow => ({
    grossText: formatGel(totals.grossTetri),
    incomeTaxText: formatGel(totals.incomeTaxTetri),
    employeePensionText: formatGel(totals.employeePensionTetri),
    employerPensionText: formatGel(totals.employerPensionTetri),
    netText: formatGel(totals.netTetri),
    stateContributionText: formatGel(totals.stateContributionTetri),
    employerCostText: formatGel(totals.employerCostTetri),
    entryTotalText: formatGel(entryTotalTetri(totals)),
    paidEmployeeCount: totals.paidEmployeeCount,
    hasStateContribution: totals.stateContributionTetri > 0,
});

// ── payroll-run list ────────────────────────────────────────────────────────

export type RunListRow = {
    /** The run's identity — its journal entry id. It has no other. */
    id: string;
    period: string;
    periodText: string;
    paidEmployeeCount: number;
    grossText: string;
    netText: string;
    employerCostText: string;
    dueDateText: string;
    journalEntryId: string;
    entryStatus: JournalEntryStatus;
    /**
     * The entry's own number, once the ledger has assigned one. A DRAFT does not
     * have one, which is the honest reason this can be empty.
     */
    entryNumberText: string;
    /**
     * The run's entry is still a DRAFT — **nothing has been charged**. The row
     * says so, because a committed run that reads like a posted one would have
     * the register claiming money that has not moved.
     */
    isUnposted: boolean;
};

/** i18n key + chip styling for a run's entry status. */
export const RUN_ENTRY_STATUS_CHIP: Record<
    JournalEntryStatus,
    { className: string; labelKey: string }
> = {
    DRAFT: {
        className: 'bg-warning-soft text-warning-soft-foreground',
        labelKey: 'salaries.runs.entryStatus.draft',
    },
    POSTED: {
        className: 'bg-success-soft text-success-soft-foreground',
        labelKey: 'salaries.runs.entryStatus.posted',
    },
    REVERSED: {
        className: 'bg-surface-secondary text-muted',
        labelKey: 'salaries.runs.entryStatus.reversed',
    },
};

export const toRunListRows = (
    runs: PayrollRunSummary[],
    t: (key: string) => string,
): RunListRow[] =>
    runs.map((run) => ({
        id: run.journalEntryId,
        period: run.period,
        periodText: formatPeriod(run.period, t),
        paidEmployeeCount: run.paidEmployeeCount,
        grossText: formatGel(run.grossTetri),
        netText: formatGel(run.netTetri),
        employerCostText: formatGel(run.employerCostTetri),
        dueDateText: isoToDisplayDate(run.dueDate),
        journalEntryId: run.journalEntryId,
        entryStatus: run.entryStatus,
        entryNumberText: run.entryNumberFormatted ?? '',
        isUnposted: run.entryStatus === 'DRAFT',
    }));

// ── refusals ────────────────────────────────────────────────────────────────

/**
 * Turn a refusal into something a bookkeeper can act on.
 *
 * **No raw 400 may reach the user**, and there are three ways one can:
 *
 * 1. A **domain refusal** — `ApiError { error, code }`. Each of the register's
 *    ten codes gets its own message under `salaries.errors.*`, because "the
 *    request was invalid" is not an answer to "why can't I run July?".
 *    `NO_RATES_IN_FORCE` is the one worth naming here: it means no rate set
 *    governs that period, and the remedy is tax settings — not a retry.
 * 2. A **journal** refusal. A payroll write that fails a *journal* rule returns
 *    the journal's code instead — `ACCOUNT_NOT_FOUND` when the chart has no
 *    account tagged for a payroll role, `AMOUNT_PRECISION` for sub-tetri money —
 *    so both vocabularies are handled and neither falls through to a generic.
 * 3. Anything else — the server's own prose, and only if there is none, a
 *    generic fallback. The server's message is preferred over a generic one
 *    wherever it exists: it names the period and the date, which no static
 *    string can.
 *
 * An unknown code is not a failure: it falls through to the server's prose,
 * which is English but specific. A new server code shows up as an unlocalized
 * sentence rather than as "something went wrong".
 */
const JOURNAL_ERROR_CODES = ['ACCOUNT_NOT_FOUND', 'AMOUNT_PRECISION'] as const;

export const salaryErrorMessage = (
    error: Pick<ErrorResponse, 'code' | 'message' | 'errors'>,
    t: (key: string, values?: Record<string, unknown>) => string,
): string => {
    if (isSalaryErrorCode(error.code)) {
        return t(`salaries.errors.${error.code}`);
    }
    if (
        typeof error.code === 'string' &&
        (JOURNAL_ERROR_CODES as readonly string[]).includes(error.code)
    ) {
        return t(`salaries.errors.${error.code}`);
    }

    if (error.message) {
        return error.message;
    }

    // ProblemDetails: `errors` is `{ FieldName: ["why"] }`. The first sentence
    // is the actionable one; joining them all produces near-duplicates.
    const fieldErrors = error.errors;
    if (fieldErrors) {
        for (const value of Object.values(fieldErrors)) {
            const first = Array.isArray(value) ? value[0] : value;
            if (typeof first === 'string' && first) {
                return first;
            }
        }
    }

    return t('salaries.errors.generic');
};

/**
 * What the register just wrote, reduced to what its banner has to say.
 *
 * The display name is resolved into a plain string before it gets here:
 * `Employee.name` is a per-locale record, and picking one of its entries is a
 * derivation, not something a View should be doing.
 */
export type SavedEmployee = {
    name: string;
    isEdit: boolean;
    /** A replayed create: the row already existed and nobody was hired. */
    alreadyExisted: boolean;
};

/**
 * Which confirmation a completed write has earned.
 *
 * Three outcomes, not one. A replayed create posted nothing and must not claim
 * a hire; an edit is not an addition. Collapsing them into "Saved" would be the
 * kind of confirmation that confirms nothing.
 */
export const savedMessageKey = (saved: SavedEmployee): string => {
    if (saved.alreadyExisted) {
        return 'salaries.result.alreadyExisted';
    }
    return saved.isEdit ? 'salaries.result.saved' : 'salaries.result.added';
};
